import React, { useState, useEffect } from 'react';
import api from '../utils/api';
import AddHolidayDialog from './AddHolidayDialog';
import './Holidays.css';

const Holidays = () => {
    const [holidays, setHolidays] = useState([]);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState('');
    const [selectedYear, setSelectedYear] = useState(new Date().getFullYear());
    const [isAddDialogOpen, setIsAddDialogOpen] = useState(false);
    const [editingHoliday, setEditingHoliday] = useState(null);
    const [deletingId, setDeletingId] = useState(null);

    const fetchHolidays = async () => {
        try {
            setLoading(true);
            setError('');
            const today = new Date();
            const dd = String(today.getDate()).padStart(2, '0');
            const mm = String(today.getMonth() + 1).padStart(2, '0');
            // Use selectedYear but keep today's dd/mm for context
            const response = await api.get(`/holidays?date=${dd}-${mm}-${selectedYear}`);
            if (response.data.success) {
                setHolidays(response.data.data);
            } else {
                setError('Failed to fetch holidays');
            }
        } catch (err) {
            setError('Error loading holidays. Please try again.');
        } finally {
            setLoading(false);
        }
    };

    useEffect(() => {
        fetchHolidays();
    }, [selectedYear]); // eslint-disable-line react-hooks/exhaustive-deps

    const handleDelete = async (id) => {
        if (!window.confirm('Delete this holiday?')) return;
        try {
            setDeletingId(id);
            await api.delete(`/holidays/${id}`);
            setHolidays(prev => prev.filter(h => h._id !== id));
        } catch (err) {
            alert('Failed to delete holiday.');
        } finally {
            setDeletingId(null);
        }
    };

    const handleSaved = (holiday, isEdit) => {
        if (isEdit) {
            setHolidays(prev => prev.map(h => h._id === holiday._id ? holiday : h)
                .sort((a, b) => new Date(a.startDate) - new Date(b.startDate)));
        } else {
            if (holiday.year === selectedYear) {
                setHolidays(prev => [...prev, holiday]
                    .sort((a, b) => new Date(a.startDate) - new Date(b.startDate)));
            }
        }
    };

    const formatDate = (dateStr) => {
        const d = new Date(dateStr);
        return d.toLocaleDateString('en-GB', { day: '2-digit', month: 'short', year: 'numeric' });
    };

    const currentYear = new Date().getFullYear();
    const yearOptions = [currentYear - 1, currentYear, currentYear + 1];

    return (
        <div className="holidays-section">
            <div className="holidays-header">
                <div className="holidays-header-text">
                    <h2>Office Holiday Calendar</h2>
                    <p>Manage class and office holidays for each academic year</p>
                </div>
                <div className="holidays-header-actions">
                    <div className="year-selector">
                        {yearOptions.map(y => (
                            <button
                                key={y}
                                className={`year-btn ${selectedYear === y ? 'active' : ''}`}
                                onClick={() => setSelectedYear(y)}
                            >
                                {y}
                            </button>
                        ))}
                    </div>
                    <button className="add-holiday-btn" onClick={() => { setEditingHoliday(null); setIsAddDialogOpen(true); }}>
                        + Add Holiday
                    </button>
                </div>
            </div>

            {loading && (
                <div className="holidays-loading">
                    <div className="loading-spinner"></div>
                    <p>Loading holidays...</p>
                </div>
            )}

            {error && (
                <div className="holidays-error">
                    <p>{error}</p>
                    <button onClick={fetchHolidays} className="retry-btn">Try Again</button>
                </div>
            )}

            {!loading && !error && (
                <>
                    {holidays.length === 0 ? (
                        <div className="holidays-empty">
                            <p>No holidays added for {selectedYear} yet.</p>
                        </div>
                    ) : (
                        <div className="holidays-table-wrap">
                            <table className="holidays-table">
                                <thead>
                                    <tr>
                                        <th>Date</th>
                                        <th>Day(s)</th>
                                        <th>Occasion</th>
                                        <th>Bengali</th>
                                        <th>Days</th>
                                        <th></th>
                                    </tr>
                                </thead>
                                <tbody>
                                    {holidays.map(h => {
                                        const isSingle = h.startDate === h.endDate ||
                                            new Date(h.startDate).toDateString() === new Date(h.endDate).toDateString();
                                        return (
                                            <tr key={h._id}>
                                                <td className="date-cell">
                                                    {isSingle
                                                        ? formatDate(h.startDate)
                                                        : `${formatDate(h.startDate)} – ${formatDate(h.endDate)}`}
                                                </td>
                                                <td className="days-count-cell">
                                                    <span className="days-badge">{h.days} {h.days === 1 ? 'day' : 'days'}</span>
                                                </td>
                                                <td className="occasion-cell">
                                                    {h.occasion}
                                                    {h.isMoonDependent && <span className="moon-mark" title="Moon-sighting dependent">*</span>}
                                                </td>
                                                <td className="occasion-bn-cell">{h.occasionBn || '—'}</td>
                                                <td></td>
                                                <td className="actions-cell">
                                                    <button
                                                        className="table-edit-btn"
                                                        onClick={() => { setEditingHoliday(h); setIsAddDialogOpen(true); }}
                                                    >
                                                        Edit
                                                    </button>
                                                    <button
                                                        className="table-delete-btn"
                                                        onClick={() => handleDelete(h._id)}
                                                        disabled={deletingId === h._id}
                                                    >
                                                        {deletingId === h._id ? '...' : 'Delete'}
                                                    </button>
                                                </td>
                                            </tr>
                                        );
                                    })}
                                </tbody>
                            </table>
                        </div>
                    )}
                </>
            )}

            <AddHolidayDialog
                isOpen={isAddDialogOpen}
                holiday={editingHoliday}
                onClose={() => { setIsAddDialogOpen(false); setEditingHoliday(null); }}
                onSaved={handleSaved}
            />
        </div>
    );
};

export default Holidays;
